import { makeStyles } from "@material-ui/core/styles"
import omit from "lodash/omit"
import moment from "moment"
import React, { useEffect, useState } from "react"
import { DateRangePicker as DateRangePickerComponent, isInclusivelyAfterDay, isInclusivelyBeforeDay } from "react-dates"
import { END_DATE, START_DATE } from "react-dates/constants"
import { Calendar } from "react-feather";
import "react-dates/initialize"
import "react-dates/lib/css/_datepicker.css"
import {
	PRIMARY_COLOR,
	SECONDARY_COLOR,
	LIGHT_GRAY_COLOR,
	SHADOW_GRAY_COLOR,
	SHADOW_PURPLE_COLOR 
} from "../../theme";

const DateRangePicker = (props) => {
	const classes = useStyles()
	const {
		periodoHandler,
		dataInicial,
		dataFinal,
		periodoMaximoEmDias,
		permitirDataFutura,
		titulo,
	} = props

	const [focusedInput, setFocusedInput] = useState(null)
	const [dataInicio, setDataInicio] = useState(dataInicial)
	const [dataFim, setDataFim] = useState(dataFinal)

	useEffect(() => {
		setDataInicio(dataInicial)
		setDataFim(dataFinal)
	}, [dataInicial, dataFinal])

	const onDatesChange = ({ startDate, endDate }) => {
		const dataInicialAlterada = startDate && (!dataInicio || !dataInicio.isSame(startDate, "day"))

		if (
			periodoMaximoEmDias &&
			endDate &&
			dataInicialAlterada &&
			isInclusivelyAfterDay(endDate, startDate.clone().add(periodoMaximoEmDias, "days"))
		) {
			endDate = null
		}

		setDataInicio(startDate)
		setDataFim(endDate)

		if (startDate === null && endDate === null) {
			periodoHandler(startDate, endDate)
		}
	}

	const onFocusChange = (input) => {
		setFocusedInput(input)
	}

	const onClose = ({ startDate, endDate }) => {
		if (startDate === null && endDate === null) {
			return;
		}
		else if (startDate !== null && endDate !== null) {
			periodoHandler(startDate, endDate)
		}
		else {
			setDataInicio(null);
			setDataFim(null);
		}
	}

	const isOutsideRange = (day) => {
		if (permitirDataFutura === false && isInclusivelyAfterDay(day, moment().add(1, "days"))) return true

		if (!periodoMaximoEmDias) return false

		if (
			focusedInput === END_DATE &&
			dataInicio &&
			isInclusivelyAfterDay(day, dataInicio.clone().add(periodoMaximoEmDias, "days"))
		)
			return true

		if (
			focusedInput === START_DATE &&
			dataFim &&
			!dataInicio &&
			isInclusivelyBeforeDay(day, dataFim.clone().add(-periodoMaximoEmDias, "days"))
		)
			return true
	}

	return (
		<div className={`${classes.picker} ${focusedInput ? "picker_focused" : ""}`}>

			<label className={`${classes.label} ${focusedInput ? "picker_focused" : ""}`}>
				{titulo}
			</label>
			<DateRangePickerComponent
				{...omit(
					props,
					"periodoHandler",
					"dataInicial",
					"permitirDataFutura",
					"dataFinal",
					"periodoMaximoEmDias",
					"titulo"
				)}
				readOnly={true}
				hideKeyboardShortcutsPanel
				startDatePlaceholderText="Início"
				endDatePlaceholderText="Fim"
				onDatesChange={onDatesChange}
				onFocusChange={onFocusChange}
				focusedInput={focusedInput}
				startDate={dataInicio}
				minimumNights={0}
				endDate={dataFim}
				isOutsideRange={isOutsideRange}
				displayFormat="DD/MM/YYYY"
				startDateId="dataInicio"
				endDateId="dataFim"
				onClose={onClose}
				initialVisibleMonth={() => {
					if (!dataFim && !dataInicio) {
						return moment()
					}
					if (focusedInput === "endDate") {
						return dataFim
					} else {
						return dataInicio
					}
				}}
				showDefaultInputIcon={!dataInicio && !dataFim}
				inputIconPosition='after'
				customInputIcon={
					dataInicio || dataFim
						? null
						: <div onClick={() => onFocusChange(START_DATE)} className={classes.icon}>
							<Calendar />
						</div>
				}
			/>
		</div>
	)
}

const borderRadius = {
	borderRadius: "4px",
	"-webkit-border-radius": "4px"
};


const useStyles = makeStyles({
	label: {
		top: "-8px",
		left: "-4px",
		lineHeight: "1",
		color: SHADOW_GRAY_COLOR,
		padding: "0 11px 0 6px",
		zIndex: "999",
		position: "absolute",
		fontSize: "1rem",
		transform: "scale(0.75)",
		backgroundColor: "white",

		"&.picker_focused": {
			color: SECONDARY_COLOR
		}
	},
	picker: {
		position: "relative",

		"& .DateInput_input::placeholder": {
			color: SHADOW_GRAY_COLOR
		},

		"& .CalendarDay__selected_span": {
			background: SHADOW_GRAY_COLOR,
			color: "white",
			border: "white 1px solid",
		},

		"& .CalendarDay__selected_span:hover": {
			background: SHADOW_PURPLE_COLOR,
		},

		"& .CalendarDay__selected": {
			background: SHADOW_PURPLE_COLOR,
			color: "white",
			border: "white 1px solid",
		},

		"& .CalendarDay__selected:hover": {
			background: SHADOW_GRAY_COLOR,
			color: "white",
		},

		"& .CalendarDay__hovered_span:hover, .CalendarDay__hovered_span": {
			background: SHADOW_PURPLE_COLOR,
		},

		"& .DateInput__small": {
			width: "48%",
			...borderRadius
		},

		"& .DateInput__small input": {
			padding: "18.5px 14px",
			paddingRight: "0px",
			border: "none",
			color: "#23144D",
			fontFamily: "'CircularStd-Book', sans-serif",
			cursor: "pointer",
			...borderRadius
		},

		"&.picker_focused .DateInput__small input": {
			padding: "17.5px 14px 17.5px 13px",
		},

		"&.picker_focused .DateInput__small input#dataFim": {
			padding: "17.5px 15px 17.5px 13px",
		},

		"& .DateRangePickerInput_clearDates_default": {
			display: "inline"
		},

		"& .DateRangePickerInput_clearDates_default:focus, .DateRangePickerInput_clearDates_default:hover": {
			background: "rgba(0, 0, 0, 0.04)",
			borderRadius: "50%"
		},

		"& .DateRangePickerInput": {
			display: "flex",
		},

		"& .DateRangePickerInput_arrow": {
			width: "4%",
			textAlign: "center",
			display: "block",
			padding: "16px 14px",
		},

		"& .DateRangePickerInput__withBorder": {
			borderColor: LIGHT_GRAY_COLOR,
			"-webkit-border-radius": "4px",
			width: "100%",
			...borderRadius
		},
		
		"&.picker_focused .DateRangePickerInput__withBorder, &.picker_focused .DateRangePickerInput__withBorder:hover": {
			borderWidth: "2px",
			borderColor: SECONDARY_COLOR,
		},

		"& .DateRangePickerInput__withBorder:hover": {
			borderColor: PRIMARY_COLOR,
		},


		"& .DateInput_fang, .DateRangePicker_picker": {
			marginTop: "10px",
		},
		"& .DateInput_fang": {
			zIndex: "4",
		},
		"& .DateRangePicker": {
			width: "100%",
			zIndex: "3",
		},

		"@media screen and (max-width: 599px)": {
			"& .DateRangePicker_picker": {
				left: "-90px !important",
				zIndex: "1200",
			},
		},
	},

	icon: {
		transform: "scale(1.2)",
		cursor: "pointer",
		color: SHADOW_GRAY_COLOR
	}
})

export default DateRangePicker
